import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, HelpCircle, Loader2, MapPin, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { COUNT_RESULT_LABELS, COUNT_RESULT_TONES, countResultOf, inventoryGaps, markProblem, procurementError } from "../lib/procurement";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { InventoryStatusBadge } from "./ProcurementInventory";

const PAGE = 100;
const FILTERS = [["all", "Tous"], ["unchecked", "À pointer"], ["found", "Trouvés"], ["absent", "Absents"], ["moved", "Ailleurs"]];
const dayText = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const HISTORY_LABELS = { inventory_opened: "Inventaire ouvert", inventory_closed: "Inventaire terminé", inventory_cancelled: "Inventaire annulé" };

function ResultBadge({ result }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${COUNT_RESULT_TONES[result]}`} data-testid={`line-result-${result}`}>{COUNT_RESULT_LABELS[result]}</span>;
}

// Found / absent are one tap; « ailleurs » asks where the asset was found.
function MarkButtons({ line, busy, onMark }) {
  const [moving, setMoving] = useState(false);
  const [location, setLocation] = useState(line.observed_location || "");
  const problem = markProblem("moved", location);
  const button = (result, label, Icon) => (
    <Button type="button" size="sm" variant={line.result === result ? "default" : "outline"} disabled={busy} onClick={() => { setMoving(false); onMark(line, result); }}
      aria-pressed={line.result === result} data-testid={`mark-${result}-${line.asset_number}`}><Icon size={14} className="mr-1" />{label}</Button>
  );
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {button("found", "Trouvé", Check)}
        {button("absent", "Absent", HelpCircle)}
        <Button type="button" size="sm" variant={line.result === "moved" ? "default" : "outline"} disabled={busy} onClick={() => setMoving((v) => !v)} aria-expanded={moving}
          data-testid={`mark-moved-${line.asset_number}`}><MapPin size={14} className="mr-1" />Ailleurs</Button>
      </div>
      {moving && (
        <div className="flex flex-wrap items-center gap-2">
          <Input value={location} maxLength={120} onChange={(e) => setLocation(e.target.value)} placeholder="Où l'avez-vous trouvé ?" className="h-9 max-w-xs" aria-label={`Lieu observé pour ${line.asset_number}`}
            data-testid={`moved-location-${line.asset_number}`} />
          <Button type="button" size="sm" disabled={busy || !!problem} onClick={() => { setMoving(false); onMark(line, "moved", location); }} data-testid={`moved-save-${line.asset_number}`}>Enregistrer</Button>
        </div>
      )}
    </div>
  );
}

function CloseDialog({ inventory, onClose, onDone }) {
  const unchecked = inventory.counts.unchecked;
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/procurement/inventories/${inventory.id}/close`, { expected_version: inventory.version, confirm_unchecked: confirm });
      toast.success("Inventaire terminé");
      onDone(data);
    } catch (e) {
      setError(procurementError(e, "Impossible de terminer l'inventaire."));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent data-testid="inventory-close-dialog">
        <DialogHeader>
          <DialogTitle>Terminer l'inventaire {inventory.number} ?</DialogTitle>
          <DialogDescription>
            Chaque pointage est inscrit dans le journal de l'actif. Les écarts ({inventoryGaps(inventory.counts)} : absents et ailleurs) sont seulement signalés — retirez ou déplacez
            un actif vous-même, avec son motif.
          </DialogDescription>
        </DialogHeader>
        {unchecked > 0 && (
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-1" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="inventory-confirm-unchecked" />
            <span>{unchecked} actif{unchecked > 1 ? "s" : ""} n'{unchecked > 1 ? "ont" : "a"} pas été pointé{unchecked > 1 ? "s" : ""}. Terminer quand même.</span>
          </label>
        )}
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Continuer le pointage</Button>
          <Button onClick={submit} disabled={busy || (unchecked > 0 && !confirm)} data-testid="inventory-close-confirm">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Terminer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ inventory, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/procurement/inventories/${inventory.id}/cancel`, { expected_version: inventory.version, reason: reason.trim() });
      toast.success("Inventaire annulé");
      onDone(data);
    } catch (e) {
      setError(procurementError(e, "Impossible d'annuler l'inventaire."));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent data-testid="inventory-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Annuler l'inventaire {inventory.number} ?</DialogTitle>
          <DialogDescription>Aucun pointage n'est inscrit dans les journaux des actifs. Vous pourrez en ouvrir un nouveau.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="inventory-cancel-reason">Motif</Label>
          <Textarea id="inventory-cancel-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="inventory-cancel-reason" />
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant="destructive" onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="inventory-cancel-confirm">Annuler l'inventaire</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProcurementInventoryDetail() {
  const { inventoryId } = useParams();
  const [filter, setFilter] = useState("all");
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [busyAsset, setBusyAsset] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);

  const params = useCallback((skip = 0) => ({ limit: PAGE, skip, ...(filter !== "all" ? { result: filter } : {}) }), [filter]);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get(`/procurement/inventories/${inventoryId}`, { params: params() });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(procurementError(e, "Impossible de charger l'inventaire."));
      setPhase("error");
    }
  }, [inventoryId, params]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const mark = async (line, result, location) => {
    setBusyAsset(line.asset_id);
    try {
      const { data: done } = await api.post(`/procurement/inventories/${inventoryId}/lines/${line.asset_id}`, { result, observed_location: result === "moved" ? (location || "").trim() : null });
      setData((prev) => ({
        ...prev, counts: done.counts,
        lines: prev.lines.map((l) => (l.asset_id === line.asset_id ? { ...l, result: done.result, observed_location: done.observed_location } : l)),
      }));
    } catch (e) {
      toast.error(procurementError(e, "Le pointage n'a pas été enregistré."));
    } finally {
      setBusyAsset(null);
    }
  };
  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get(`/procurement/inventories/${inventoryId}`, { params: params(data.lines.length) });
      setData((prev) => ({ ...body, lines: [...prev.lines, ...body.lines.filter((l) => !prev.lines.some((p) => p.asset_id === l.asset_id))] }));
    } catch (e) {
      toast.error(procurementError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3 max-w-xl mx-auto" data-testid="inventory-detail-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{error}</p>
        <div className="flex justify-center gap-2"><Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button><Button variant="ghost" asChild><Link to="/achats/inventaire">Retour</Link></Button></div>
      </Card>
    );
  }
  if (!data) return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-64 w-full" /></div>;

  const open = data.status === "open";
  const canWrite = open && data.can_manage;
  const c = data.counts;
  return (
    <div className="space-y-4" data-testid="inventory-detail-page">
      <div>
        <Link to="/achats/inventaire" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} />Inventaire des actifs</Link>
        <div className="flex flex-wrap items-start justify-between gap-3 mt-1">
          <div className="min-w-0">
            <h1 className="font-display font-bold text-2xl text-slate-900 flex flex-wrap items-center gap-2">{data.number}<InventoryStatusBadge status={data.status} /></h1>
            <p className="text-sm text-slate-500">{data.note ? `${data.note} · ` : ""}Ouvert le {dayText(data.opened_at)}{data.opened_by_name ? ` par ${data.opened_by_name}` : ""}</p>
          </div>
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setDialog("cancel")} data-testid="inventory-cancel">Annuler l'inventaire</Button>
              <Button onClick={() => setDialog("close")} data-testid="inventory-close">Terminer</Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-3 min-w-0">
          <div className="flex gap-1 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0" role="tablist" aria-label="Filtrer les actifs">
            {FILTERS.map(([id, label]) => (
              <button key={id} type="button" role="tab" aria-selected={filter === id} onClick={() => setFilter(id)} data-testid={`inventory-filter-${id}`}
                className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap shrink-0 ${filter === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
                {label}<span className="ml-1.5 tabular-nums opacity-80">{id === "all" ? c.total : c[id]}</span>
              </button>
            ))}
          </div>
          {phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
          {phase === "ready" && data.lines.length === 0 && <Card className="p-6 text-center text-sm text-slate-500" data-testid="inventory-lines-empty">Aucun actif dans cette vue.</Card>}
          {phase === "ready" && data.lines.length > 0 && (
            <Card className="divide-y divide-slate-100" data-testid="inventory-lines">
              {data.lines.map((line) => (
                <div key={line.asset_id} className="p-3 sm:p-4 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`inventory-line-${line.asset_number}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/achats/actifs/${line.asset_id}`} className="font-medium text-slate-900 hover:underline break-words">{line.label}</Link>
                      <ResultBadge result={countResultOf(line)} />
                    </div>
                    <div className="text-xs text-slate-500">
                      {line.asset_number} · {line.expected_holder || "En stock"}{line.expected_location ? ` · ${line.expected_location}` : ""}
                      {line.result === "moved" && line.observed_location ? ` · trouvé : ${line.observed_location}` : ""}
                    </div>
                  </div>
                  {canWrite && <MarkButtons key={`${line.asset_id}-${line.result}`} line={line} busy={busyAsset === line.asset_id} onMark={mark} />}
                </div>
              ))}
            </Card>
          )}
          {phase === "ready" && data.lines.length < data.lines_total && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{data.lines.length} sur {data.lines_total}</span>
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
            </div>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <Card className="p-4 sm:p-5 space-y-3" data-testid="inventory-summary">
            <h2 className="font-semibold text-slate-900">Pointage</h2>
            <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              {[["Actifs", c.total], ["À pointer", c.unchecked], ["Trouvés", c.found], ["Absents", c.absent], ["Ailleurs", c.moved]].map(([label, value]) => (
                <div key={label} className="contents"><dt className="text-slate-500">{label}</dt><dd className="text-right tabular-nums font-medium text-slate-900">{value}</dd></div>
              ))}
            </dl>
            {open && <p className="text-xs text-slate-500">Terminer inscrit chaque pointage dans le journal de l'actif. Un actif absent ou ailleurs n'est ni retiré ni déplacé tout seul.</p>}
            {data.status === "closed" && <p className="text-xs text-slate-600" data-testid="inventory-gaps">Terminé le {dayText(data.closed_at)}{data.closed_by_name ? ` par ${data.closed_by_name}` : ""} · {inventoryGaps(c)} écart{inventoryGaps(c) > 1 ? "s" : ""}.</p>}
            {data.status === "cancelled" && <p className="text-xs text-slate-600">Annulé le {dayText(data.cancelled_at)} : {data.cancel_reason}</p>}
          </Card>
          <Card className="p-4 sm:p-5 space-y-2" data-testid="inventory-history">
            <h2 className="font-semibold text-slate-900">Historique</h2>
            <ol className="space-y-2">
              {data.history.map((h) => (
                <li key={h.id} className="text-sm"><span className="text-slate-900">{HISTORY_LABELS[h.kind] || h.kind}</span>
                  <div className="text-xs text-slate-500">{dayText(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}{h.reason ? ` · ${h.reason}` : ""}</div></li>
              ))}
            </ol>
          </Card>
        </aside>
      </div>
      {dialog === "close" && <CloseDialog inventory={data} onClose={() => setDialog(null)} onDone={(body) => { setDialog(null); setData(body); }} />}
      {dialog === "cancel" && <CancelDialog inventory={data} onClose={() => setDialog(null)} onDone={(body) => { setDialog(null); setData(body); }} />}
    </div>
  );
}
